// The card's aggregator, one instance per stage: for each scanned game of the
// period that reached the stage, the reader's expected score (win chance,
// 0–100, Accuracy's conversion) when the stage started and when it ended, and
// the offset between them: what the stage gained or lost against what the
// position promised. Registered with the game store by this plugin's setup;
// the store runs it over the last 30 days' games.
//
// Start: the reader's score at the stage's first position (the curve's first
// scored ply, `StageCurve.scores`).
//
// End, the middlegame (and the opening): the score of the stage's last
// position when the game went on to the next stage (the position taken into
// it); when the game finished in the stage (no next stage: mate, resignation,
// timeout, agreed draw, abandonment), the result (loss 0, draw 50, win 100).
//
// End, the ending (where every game finishes): the result. With the date
// across, the offset needs no board score to spread the dots: the start
// varies from game to game, so result − start does too.
//
// Offset: end − start, in percentage points of expected score, −100…+100.
// 0 is no change; below, the stage lost value; above, it
// gained (a save, a swindle).
import { winPercent } from '@chess/api-insights';
import { aggregatorId, collect, type Accumulator, type Aggregator, type EndingCase, type EndingType, type GameFacts, type GameOutcome, type Phase } from '@chess/api-model';

const RESULT_SCORE: Record<GameOutcome, number> = { win: 100, draw: 50, loss: 0 };
const NEXT: Record<Phase, Phase | undefined> = { opening: 'middlegame', middlegame: 'ending', ending: undefined };

/** One game's stage, from the reader's side. */
export interface StageScatterPoint {
  /** The game's id, a stable key. */
  id: string;
  /** Epoch milliseconds. */
  at: number;
  outcome: GameOutcome;
  opponent?: string;
  /** 0–100: the reader's expected score when the stage started. */
  start: number;
  /** 0–100: the same when it ended (or the result, see the rules above). */
  end: number;
  /** end − start, in percentage points: −100…+100, 0 is no change. */
  offset: number;
  /** The game finished in this stage (always so for the ending). */
  finishedHere: boolean;
  /** The review at the stage's first move, for a click on the point. */
  reviewPath?: string;
  /** The ending stage only: the kind of ending and how it went. */
  endingType?: EndingType;
  endingCase?: EndingCase;
}

/** What the card shows; plain data, so the card and its stories need no store. */
export interface StageScatterResult {
  /** Oldest first. */
  points: StageScatterPoint[];
}

/** The game's stage as a point, or undefined when it has no scored stage. */
export function stageScatterPoint(stage: Phase, g: GameFacts): StageScatterPoint | undefined {
  const curve = g.scanned ? g.stageCurves?.[stage] : undefined;
  if (!curve) return undefined;
  const scored = curve.scores.filter((s): s is number => s !== null);
  if (!scored.length) return undefined;
  const next = NEXT[stage];
  const finishedHere = !next || !g.stageCurves?.[next];
  const start = winPercent(scored[0]);
  const board = winPercent(scored[scored.length - 1]);
  const result = RESULT_SCORE[g.outcome];
  const end = stage === 'ending' || finishedHere ? result : board;
  return {
    id: g.id,
    at: g.playedAt,
    outcome: g.outcome,
    ...(g.opponent ? { opponent: g.opponent } : {}),
    start,
    end,
    offset: end - start,
    finishedHere,
    ...(g.reviewPath ? { reviewPath: `${g.reviewPath}#ply=${curve.fromPly + 1}` } : {}),
    ...(stage === 'ending' && g.endingType ? { endingType: g.endingType } : {}),
    ...(stage === 'ending' && g.endingCase ? { endingCase: g.endingCase } : {}),
  };
}

export class StageScatterAggregator implements Aggregator<StageScatterResult> {
  readonly id;
  readonly stage: Phase;

  constructor(stage: Phase) {
    this.stage = stage;
    this.id = aggregatorId(`stage-scatter-${stage}`);
  }

  start(): Accumulator<StageScatterResult> {
    return collect((games) => {
      const points = games
        .map((g) => stageScatterPoint(this.stage, g))
        .filter((p): p is StageScatterPoint => p !== undefined)
        .sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
      return { points };
    });
  }
}
